import { FullDashboard } from "../../lib/entries/types";
import { isVisualParam } from "../../lib/visuals/visuals";
import {
  ParamWidget,
  TextWidget,
  VisualizationWidget,
} from "../../lib/widgets/types";
import { Layout } from "react-grid-layout";
import produce from "immer";
import { Size } from "../theme/theme";

export type Layouts = ReturnType<typeof createLayouts>;

export const widgetKey = (
  widget: TextWidget | VisualizationWidget | ParamWidget
) => {
  return String(widget.id);
};

// Creates different layouts for different breakpoints, necessary for Responsive Grid Layout
// These are the layouts used by Grid Layout to put the widgets in the correct places
// The values w, h, x, y are defined in grid units, not pixels
// w: width -> number of columns the widget will occupy in the dashboard
// h: height -> number of rows the widget will occupy in the dashboard
// x: column position -> where in the grid column the widget should be placed
// y: row position -> in which row of the grid the widget should be placed
export const createLayouts = (updatedDashboard: FullDashboard) => {
  const widgets = [
    ...(updatedDashboard.param_widgets || []),
    ...(updatedDashboard.text_widgets || []),
    ...(updatedDashboard.visualization_widgets || []),
  ];

  // Ordering the widgets by their column and row, so that they keep the order when the screen is smaller.
  widgets.sort((w1, w2) => {
    if (
      w1.options.position.col !== undefined &&
      w2.options.position.col !== undefined &&
      w2.options.position.row !== undefined &&
      w1.options.position.row !== undefined
    ) {
      return (
        w1.options.position.row - w2.options.position.row ||
        w1.options.position.col - w2.options.position.col
      );
    }

    return 0;
  });

  // On smaller screens
  // w: width is 1, because only one column exists in these screens
  // h: is either the height (sizeY) defined for the widget or it will occupy two rows
  //    if it's a ParamWidget, we are enforcing the widget to only have 1 row
  // x: it will always be the first position, as there's only one column
  // y: if it has a row position defined it will get that, otherwise it will be put in the last row

  const sm = widgets.map((w) => {
    const defaultHeight = isVisualParam(w.visualization)
      ? 1
      : w.options.position.sizeY ?? 2;

    return {
      i: widgetKey(w),
      w: 1,
      h: defaultHeight,
      x: 0,
      y: w.options.position.row ?? Infinity,
    };
  });

  // On larger screens
  // w: either the width (sizeX) defined for the widget or it will occupy two columns.
  // h: is either the height (sizeY) defined for the widget or it will occupy two rows
  //    if it's a ParamWidget, we are enforcing the widget to only have 1 row
  // x: it will default to 0, so all elements will be put on the first column, in case it is not defined which column they belong to
  // y: if it has a row position defined it will get that, otherwise it will be put in the last row
  const lg = widgets.map((w) => {
    const height = isVisualParam(w.visualization)
      ? 1
      : w.options.position.sizeY ?? 2;

    return {
      i: widgetKey(w),
      w: w.options.position.sizeX ?? 2,
      h: height,
      x: w.options.position.col ?? 0,
      y: w.options.position.row ?? Infinity,
    };
  });

  return { sm, lg };
};

export const parseLayouts = (
  dashboard: FullDashboard,
  breakpoint: Breakpoint,
  layouts: Layout[]
) => {
  const text_widgets = dashboard.text_widgets || [];
  const visualization_widgets = dashboard.visualization_widgets || [];
  const param_widgets = dashboard.param_widgets || [];

  const updatedTextWidgets = updateWidgetsLayouts(
    text_widgets,
    layouts,
    breakpoint
  );

  const updatedVisualizationWidgets = updateWidgetsLayouts(
    visualization_widgets,
    layouts,
    breakpoint
  );

  const updatedParamWidgets = updateWidgetsLayouts(
    param_widgets,
    layouts,
    breakpoint
  );

  return {
    ...dashboard,
    text_widgets: updatedTextWidgets,
    visualization_widgets: updatedVisualizationWidgets,
    param_widgets: updatedParamWidgets,
  };
};

const updateWidgetsLayouts = <
  T extends VisualizationWidget | TextWidget | ParamWidget
>(
  widgets: T[],
  layouts: Layout[],
  breakpoint: Breakpoint
): T[] => {
  const updatedWidgets = widgets.map((widget) => {
    return produce(widget, (draft) => {
      const layout = layouts.find((l) => {
        return l.i === widgetKey(widget);
      });
      if (layout) {
        draft.options.position.row = layout.y;
        // We don't allow users to change the height of params
        if (!isVisualParam(widget.visualization)) {
          draft.options.position.sizeY = layout.h;
        }
        if (breakpoint === "lg") {
          draft.options.position.col = layout.x;
          draft.options.position.sizeX = layout.w;
        }
      }
    });
  });

  return updatedWidgets;
};

export const parseBreakpoint = (input: string): Breakpoint | undefined => {
  if (input === "sm" || input === "lg") {
    return input;
  }
};

export type Breakpoint = Extract<Size, "sm" | "lg">;
